import { useState } from "react";
import { API } from '../../config/api';
import axios from "axios";
import { errorMessage } from "../../utils/errorMessage";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { SubmissionLayout, StatusBadge } from "../../components/SubmissionLayout";
import BonsaiMate from "../../components/BonsaiMate";
import {
  FileSpreadsheet, Loader2, CheckCircle2, ExternalLink,
  Banknote, Building2, FileDown, XCircle, AlertTriangle, Clock, ShieldCheck,
  ShieldAlert, Eye, CheckSquare, Square, Receipt
} from "lucide-react";

function FinancialRow({ label, value, accent = false, bold = false, highlight = false }) {
  return (
    <div className={`flex justify-between items-center py-2 ${highlight ? 'bg-teal text-white rounded-md px-4 mt-2' : 'border-b border-white/10/40 last:border-0'} ${bold ? 'font-semibold' : ''}`}>
      <span className={highlight ? 'text-white' : 'text-sm text-txt-faint'}>{label}</span>
      <span className={`tabular-nums ${highlight ? 'text-white font-bold' : accent ? 'text-sm font-medium text-teal' : 'text-sm font-medium text-txt-faint'}`}>
        {value}
      </span>
    </div>
  );
}

function CSStatusBadge({ status }) {
  const map = {
    submitted: { label: "Awaiting Independent Check", color: "bg-status-amber-dim text-status-amber border-amber-300", icon: Clock },
    checked: { label: "Independently Verified", color: "bg-teal-dim text-teal border-teal", icon: ShieldCheck },
    authorised: { label: "Authorised (Override)", color: "bg-teal-dim text-teal border-teal", icon: ShieldCheck },
    approved: { label: "Approved", color: "bg-teal-dim text-teal border-teal", icon: CheckCircle2 },
    rejected: { label: "Returned for Amendment", color: "bg-status-red-dim text-status-red border-red-300", icon: XCircle },
    draft: { label: "Draft", color: "bg-slate-100 text-slate-600 border-slate-300", icon: FileSpreadsheet },
  };
  const s = map[status] || map.draft;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${s.color}`} data-testid="cs-status-badge">
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
}

export default function CompletionStatementDetail({ submission, formatDate, navigate, isDirector, onDelete, currentUserEmail, currentUserName }) {
  // BM-H3521: there is no completion statement document generator, so the
  // page no longer offers Generate & File or Download DOCX. A record that
  // already carries a generated document still shows its banner.
  const generated = !!submission?.document_generated;
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(submission?.status || "submitted");

  // Two-person control: checker confirmations
  const [lenderChecked, setLenderChecked] = useState(false);
  const [ledgerChecked, setLedgerChecked] = useState(false);
  const [bankChecked, setBankChecked] = useState(false);
  const [checkerNotes, setCheckerNotes] = useState("");
  const [checking, setChecking] = useState(false);

  // COLP override
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overriding, setOverriding] = useState(false);

  const data = submission?.data || {};
  const header = data.header || {};
  const consideration = data.consideration || {};
  const totals = data.totals || {};

  const fmt = (v) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(parseFloat(v) || 0);

  // Determine if current user is the preparer
  const preparerEmail = (submission?.prepared_by_email || submission?.submitted_by_email || "").toLowerCase();
  const isSelfPrepared = currentUserEmail && preparerEmail && currentUserEmail.toLowerCase() === preparerEmail;

  const handleApprove = async () => {
    setApproving(true);
    try {
      // BM-H3521: the server takes the actor from the session, not the body.
      const res = await axios.post(`${API}/completion-statement/${submission.id}/approve`, {});
      if (res.data?.success) {
        setCurrentStatus(res.data.status || "checked");
        toast.success("Completion statement approved.");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Approval failed."));
    } finally { setApproving(false); }
  };

  const handleCheck = async () => {
    if (!lenderChecked || !ledgerChecked || !bankChecked) {
      toast.error("All three verification checks must be confirmed.");
      return;
    }
    setChecking(true);
    try {
      const res = await axios.post(`${API}/completion-statement/${submission.id}/check`, {
        lender_statement_compared: lenderChecked,
        ledger_compared: ledgerChecked,
        bank_entry_compared: bankChecked,
        notes: checkerNotes.trim(),
      });
      if (res.data?.success) {
        setCurrentStatus("checked");
        toast.success(res.data.message || "Completion statement independently verified.");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Check failed."));
    } finally { setChecking(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for returning the statement.");
      return;
    }
    setRejecting(true);
    try {
      const res = await axios.post(`${API}/completion-statement/${submission.id}/reject`, {
        reason: rejectReason.trim(),
      });
      if (res.data?.success) {
        setCurrentStatus("rejected");
        setShowRejectForm(false);
        toast.success("Statement returned for amendment.");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Rejection failed."));
    } finally { setRejecting(false); }
  };

  const handleOverride = async () => {
    if (overrideReason.trim().length < 20) {
      toast.error("Override reason must be at least 20 characters.");
      return;
    }
    setOverriding(true);
    try {
      const res = await axios.post(`${API}/completion-statement/${submission.id}/override`, {
        reason: overrideReason.trim(),
      });
      if (res.data?.success) {
        setCurrentStatus("authorised");
        setShowOverrideForm(false);
        toast.success(res.data.message || "Override applied. The statement is authorised.");
      }
    } catch (err) {
      toast.error(errorMessage(err, "Override failed."));
    } finally { setOverriding(false); }
  };

  const handleDownloadPdf = async () => {
    if (submission?.pdf_sharepoint_url) {
      window.open(submission.pdf_sharepoint_url, "_blank");
      return;
    }
    toast.info("PDF is available on SharePoint in the Completions folder.");
  };

  const actions = (
    <>
      {/* BM-H3521: Download DOCX and Generate & File called routes that never
          existed and there is no generator to wire them to, so they are gone
          until one is built. */}
      <span className="text-xs text-txt-faint" data-testid="cs-no-document-note">
        The statement document is not produced here. Prepare it outside CaseMinder.
      </span>
      {["checked", "authorised", "approved"].includes(currentStatus) && submission?.pdf_sharepoint_url && (
        <Button variant="outline" size="sm" onClick={handleDownloadPdf} className="border-blue-500 text-blue-600 hover:bg-blue-50 gap-1 text-xs h-8" data-testid="cs-download-pdf-btn">
          <FileDown className="w-3.5 h-3.5" /> PDF (SharePoint)
        </Button>
      )}
      {currentStatus !== "rejected" && (
        // 27 Oct 2026 (May Ann): raise the invoice request from here,
        // filled in from this statement. A request already raised from it
        // is opened instead (the request page checks).
        <Button size="sm" onClick={() => navigate(`/invoice-request?${new URLSearchParams({ matter: header.matter_reference || submission.matter_reference || "", cs: submission.id })}`)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1 text-xs h-8" data-testid="cs-raise-invoice-btn"
          title="Raise invoice request from this statement">
          <Receipt className="w-3.5 h-3.5" /> Raise invoice request
        </Button>
      )}
      {currentStatus === "rejected" && (
        <Button variant="outline" size="sm" onClick={() => navigate(`/completion-statement?matter_id=${submission.id}`)}
          className="border-navy-border text-teal hover:bg-teal/5 gap-1 text-xs h-8" data-testid="cs-revise-btn">
          Revise
        </Button>
      )}
    </>
  );

  return (
    <>
    <SubmissionLayout
      navigate={navigate}
      title={header.matter_reference || "Completion Statement"}
      subtitle={`${header.client_name || ""} \u2014 ${header.property_address || ""}`}
      matterReference={header.matter_reference}
      badge="Completion Statement"
      badgeColor="emerald"
      badgeIcon={FileSpreadsheet}
      isTest={submission?.is_test_submission}
      isDirector={isDirector}
      onDelete={onDelete}
      actions={actions}
    >
      {/* ── Two-Person Control: Independent Check Panel ── */}
      {currentStatus === "submitted" && !isSelfPrepared && (
        <div className="bg-status-amber-dim/90 border border-amber-200 rounded-md p-5 space-y-4" data-testid="cs-checker-panel">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 text-status-amber shrink-0" />
            <div>
              <p className="text-sm font-semibold text-status-amber">Independent Check Required</p>
              <p className="text-xs text-status-amber">
                Prepared by {submission?.prepared_by_name || header.fee_earner || "Fee Earner"} on {formatDate(submission?.created_at)}.
                Please verify the figures below before approving.
              </p>
            </div>
          </div>

          <div className="space-y-2.5 ml-1">
            <label className="flex items-center gap-2.5 cursor-pointer select-none group" data-testid="cs-check-lender">
              <button type="button" onClick={() => setLenderChecked(!lenderChecked)} className="focus:outline-none">
                {lenderChecked
                  ? <CheckSquare className="w-5 h-5 text-teal" />
                  : <Square className="w-5 h-5 text-slate-400 group-hover:text-status-amber" />}
              </button>
              <span className="text-sm text-txt-faint">Lender completion statement compared and agrees</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer select-none group" data-testid="cs-check-ledger">
              <button type="button" onClick={() => setLedgerChecked(!ledgerChecked)} className="focus:outline-none">
                {ledgerChecked
                  ? <CheckSquare className="w-5 h-5 text-teal" />
                  : <Square className="w-5 h-5 text-slate-400 group-hover:text-status-amber" />}
              </button>
              <span className="text-sm text-txt-faint">Client ledger balanced and compared</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer select-none group" data-testid="cs-check-bank">
              <button type="button" onClick={() => setBankChecked(!bankChecked)} className="focus:outline-none">
                {bankChecked
                  ? <CheckSquare className="w-5 h-5 text-teal" />
                  : <Square className="w-5 h-5 text-slate-400 group-hover:text-status-amber" />}
              </button>
              <span className="text-sm text-txt-faint">Bank account entries verified against statement</span>
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium text-txt-faint mb-1">Notes (optional)</label>
            <textarea
              value={checkerNotes}
              onChange={(e) => setCheckerNotes(e.target.value)}
              placeholder="Any observations or notes..."
              rows={2}
              className="w-full rounded-md border border-amber-300 px-3 py-2 text-sm focus:ring-amber-500 focus:border-amber-500"
              data-testid="cs-checker-notes"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm" onClick={handleCheck} disabled={checking || !(lenderChecked && ledgerChecked && bankChecked)}
              className="bg-teal hover:bg-teal-hover text-white gap-1.5 text-xs h-9 disabled:bg-slate-400"
              data-testid="cs-check-confirm-btn"
            >
              {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
              Confirm Independent Check
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => setShowRejectForm(true)}
              className="border-red-300 text-status-red hover:bg-status-red-dim gap-1 text-xs h-9"
              data-testid="cs-reject-btn"
            >
              <XCircle className="w-3.5 h-3.5" /> Return for Amendment
            </Button>
          </div>

          {showRejectForm && (
            <div className="space-y-3 border-t border-amber-200 pt-4 mt-2">
              <label className="block text-sm font-medium text-status-amber">Reason for return:</label>
              <textarea
                value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain what needs amending..."
                rows={3}
                className="w-full rounded-md border border-amber-300 px-3 py-2 text-sm focus:ring-amber-500 focus:border-amber-500"
                data-testid="cs-reject-reason"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleReject} disabled={rejecting}
                  className="bg-status-red hover:bg-status-red text-white gap-1 text-xs h-8" data-testid="cs-reject-confirm-btn">
                  {rejecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Send Back
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setShowRejectForm(false); setRejectReason(""); }} className="text-xs h-8">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Self-Prepared Notice: You cannot check your own work ── */}
      {currentStatus === "submitted" && isSelfPrepared && (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-4 flex items-center gap-3" data-testid="cs-self-check-notice">
          <Eye className="w-5 h-5 text-slate-500 shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-700">Awaiting Independent Check</p>
            <p className="text-xs text-slate-500">
              This statement must be verified by a different person before it can proceed. You cannot check your own work.
            </p>
          </div>
        </div>
      )}

      {/* ── Legacy Approval Panel (for directors when status = submitted, in case /check isn't used directly) ── */}
      {isDirector && currentStatus === "submitted" && !isSelfPrepared && (
        <div className="bg-blue-50/80 border border-blue-200 rounded-md p-4" data-testid="cs-legacy-approval-panel">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800">Director Quick Actions</p>
              <p className="text-xs text-blue-600">
                Use the Independent Check panel above, or approve directly.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" onClick={handleApprove} disabled={approving}
              className="bg-teal hover:bg-teal-hover text-white gap-1 text-xs h-8" data-testid="cs-approve-btn">
              {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Approve
            </Button>
          </div>
        </div>
      )}

      {/* ── COLP Override (Directors only, when status = submitted) ── */}
      {isDirector && currentStatus === "submitted" && (
        <div className="border border-dashed border-slate-300 rounded-md p-4" data-testid="cs-override-section">
          {!showOverrideForm ? (
            <button onClick={() => setShowOverrideForm(true)}
              className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="cs-override-toggle">
              COLP Override (exceptional use only)
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-status-amber" />
                <p className="text-sm font-medium text-orange-700">COLP Override</p>
              </div>
              <p className="text-xs text-slate-600">
                This bypasses two-person control. Provide a mandatory reason (min 20 characters). This action is permanently logged.
              </p>
              <textarea
                value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Reason for override (minimum 20 characters)..."
                rows={2}
                className="w-full rounded-md border border-orange-300 px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500"
                data-testid="cs-override-reason"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleOverride} disabled={overriding || overrideReason.trim().length < 20}
                  className="bg-status-amber hover:bg-status-amber text-white gap-1 text-xs h-8 disabled:bg-slate-400" data-testid="cs-override-confirm-btn">
                  {overriding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />} Apply Override
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setShowOverrideForm(false); setOverrideReason(""); }} className="text-xs h-8">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Checked / Verified Banner ── */}
      {(currentStatus === "checked" || currentStatus === "authorised") && (
        <div className="bg-teal-dim/80 border border-teal rounded-md p-4" data-testid="cs-checked-banner">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-teal shrink-0" />
            <div className="text-sm text-teal">
              <span className="font-medium">
                {currentStatus === "authorised" ? "Authorised via COLP Override" : "Independently Verified"}
              </span>
              {(submission?.checked_at || submission?.override_at) && (
                <span className="ml-1 text-teal">
                  on {formatDate(submission.checked_at || submission.override_at)}
                </span>
              )}
              {submission?.checked_by_name && (
                <span className="ml-1 text-teal">by {submission.checked_by_name}</span>
              )}
              {submission?.override_by_name && currentStatus === "authorised" && (
                <span className="ml-1 text-teal">by {submission.override_by_name}</span>
              )}
              {submission?.pdf_sharepoint_url && (
                <a href={submission.pdf_sharepoint_url} target="_blank" rel="noopener noreferrer" className="ml-2 underline text-blue-600 hover:text-blue-800">
                  View PDF on SharePoint <ExternalLink className="inline h-3 w-3" />
                </a>
              )}
            </div>
          </div>
          {submission?.checker_confirmations && (
            <div className="mt-3 ml-8 grid grid-cols-1 gap-1 text-xs text-teal">
              <span><CheckCircle2 className="inline w-3 h-3 mr-1" /> Lender statement compared</span>
              <span><CheckCircle2 className="inline w-3 h-3 mr-1" /> Client ledger balanced</span>
              <span><CheckCircle2 className="inline w-3 h-3 mr-1" /> Bank entries verified</span>
              {submission.checker_confirmations.notes && (
                <span className="mt-1 text-teal italic">Notes: {submission.checker_confirmations.notes}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Approved Banner (legacy) ── */}
      {currentStatus === "approved" && (
        <div className="bg-teal-dim/80 border border-teal rounded-md p-4 flex items-center gap-3" data-testid="cs-approved-banner">
          <CheckCircle2 className="w-5 h-5 text-teal shrink-0" />
          <div className="text-sm text-teal">
            <span className="font-medium">Approved</span>
            {submission?.approved_at && <span className="ml-1 text-teal">on {formatDate(submission.approved_at)}</span>}
            {submission?.pdf_sharepoint_url && (
              <a href={submission.pdf_sharepoint_url} target="_blank" rel="noopener noreferrer" className="ml-2 underline text-blue-600 hover:text-blue-800">
                View PDF on SharePoint <ExternalLink className="inline h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* ── Rejected Banner ── */}
      {currentStatus === "rejected" && (
        <div className="bg-status-red-dim/80 border border-red-200 rounded-md p-4" data-testid="cs-rejected-banner">
          <div className="flex items-center gap-3">
            <XCircle className="w-5 h-5 text-status-red shrink-0" />
            <div className="text-sm text-status-red">
              <span className="font-medium">Returned for Amendment</span>
              {submission?.rejected_at && <span className="ml-1 text-status-red">on {formatDate(submission.rejected_at)}</span>}
              {submission?.rejected_by_name && <span className="ml-1 text-status-red">by {submission.rejected_by_name}</span>}
            </div>
          </div>
          {submission?.rejection_reason && (
            <div className="mt-3 ml-8 p-3 bg-status-red-dim/60 rounded text-sm text-status-red border-l-4 border-red-400">
              {submission.rejection_reason}
            </div>
          )}
        </div>
      )}

      {/* ── Generated Banner (DOCX) ── */}
      {generated && (
        <div className="bg-teal-dim/80 border border-teal rounded-md p-4 flex items-center gap-3" data-testid="cs-generated-banner">
          <CheckCircle2 className="w-5 h-5 text-teal shrink-0" />
          <div className="text-sm text-teal">
            <span className="font-medium">Document generated</span>
            {submission?.sharepoint_web_url && (
              <a href={submission.sharepoint_web_url} target="_blank" rel="noopener noreferrer" className="ml-2 underline text-teal hover:text-teal">
                Open in SharePoint <ExternalLink className="inline h-3 w-3" />
              </a>
            )}
            {submission?.document_generated_at && (
              <span className="ml-2 text-teal">({formatDate(submission.document_generated_at)})</span>
            )}
          </div>
        </div>
      )}

      {/* ── Status Row ── */}
      <div className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 px-5 py-3.5 shadow-sm" data-testid="cs-header-info">
        <div className="flex items-center gap-4">
          <CSStatusBadge status={currentStatus} />
          <span className="text-sm text-txt-faint">Submitted {formatDate(submission.created_at)}</span>
          {submission?.prepared_by_name && (
            <span className="text-xs text-txt-faint">Prepared by {submission.prepared_by_name}</span>
          )}
          {submission?.is_test_submission && (
            <span className="text-xs px-2 py-0.5 bg-status-amber-dim text-orange-600 rounded font-medium">Test</span>
          )}
        </div>
        <span className="text-xs text-txt-muted font-mono tracking-wide">{submission.id?.slice(0, 8)}</span>
      </div>

      {/* ── Bento Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="cs-bento-grid">
        {/* Matter Details */}
        <div className="md:col-span-8 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-matter-details">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Building2 className="w-4 h-4" /> Matter Details
            </h2>
          </div>
          <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            {[
              { label: "Matter Ref", value: header.matter_reference },
              { label: "Client", value: header.client_name },
              { label: "Counterparty", value: header.counterparty_name },
              { label: "Acting For", value: header.client_role },
              { label: "Transaction", value: (header.transaction_type || "").replace(/_/g, " ") },
              { label: "Completion Date", value: header.completion_date },
              { label: "Property", value: header.property_address },
              { label: "Fee Earner", value: header.fee_earner },
            ].map(({ label, value }) => (
              <div key={label}>
                <span className="text-xs font-medium text-txt-faint uppercase tracking-wider block mb-0.5">{label}</span>
                <span className="font-medium text-txt-faint capitalize">{value || "\u2014"}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Net Balance */}
        <div className="md:col-span-4 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-net-balance">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Banknote className="w-4 h-4" /> Net Balance
            </h2>
          </div>
          <div className="p-5 text-center space-y-3">
            <div className="text-2xl font-bold text-teal">{fmt(totals.net_completion_balance)}</div>
            <p className="text-sm text-txt-faint">
              {totals.balance_direction === "to_client" ? "Balance due to you on completion" : "Balance required from you on completion"}
            </p>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="md:col-span-12 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-financial-summary">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <FileSpreadsheet className="w-4 h-4" /> Financial Summary
            </h2>
          </div>
          <div className="p-5 max-w-2xl">
            <FinancialRow
              label={totals.direction === "receiving" ? "Sale Price / Consideration" : "Purchase Price / Consideration"}
              value={fmt(totals.consideration_amount)} bold
            />
            {(consideration.adjustments || []).length > 0 && (
              <div className="pl-4 mt-1 space-y-0.5 text-sm text-txt-faint">
                {(consideration.adjustments || []).map((a, i) => (
                  <FinancialRow key={i} label={`${a.type === "add" ? "+" : "-"} ${a.description}`} value={fmt(a.amount)} />
                ))}
              </div>
            )}
            {totals.legal_fees_gross > 0 && <FinancialRow label="Legal Fees" value={fmt(totals.legal_fees_gross)} />}
            {totals.stamp_duty > 0 && <FinancialRow label="Stamp Duty" value={fmt(totals.stamp_duty)} />}
            {totals.disbursements_gross > 0 && <FinancialRow label="Disbursements" value={fmt(totals.disbursements_gross)} />}
            {totals.redemptions_total > 0 && <FinancialRow label="Redemptions" value={fmt(totals.redemptions_total)} />}
            {totals.retentions_total > 0 && <FinancialRow label="Retentions" value={fmt(totals.retentions_total)} />}
            {totals.payments_out_total > 0 && <FinancialRow label="Other Payments Out" value={fmt(totals.payments_out_total)} />}
            {totals.total_existing_monies > 0 && <FinancialRow label="Existing Monies / Funds" value={fmt(totals.total_existing_monies)} />}
            <FinancialRow
              label={totals.balance_direction === "to_client" ? "Balance due to you on completion" : "Balance required from you on completion"}
              value={fmt(totals.net_completion_balance)} highlight
            />
          </div>
        </div>
      </div>
    </SubmissionLayout>
    {header.matter_reference && (
      <BonsaiMate context="matter" matterReference={header.matter_reference} />
    )}
    </>
  );
}
